import { useState, useEffect, useCallback } from "react";

export type ColorblindMode = "normal" | "protanopia" | "deuteranopia" | "tritanopia" | "achromatopsia";

export const COLORBLIND_LABELS: Record<ColorblindMode, string> = {
  normal: "Normal Vision",
  protanopia: "Protanopia (Red-blind)",
  deuteranopia: "Deuteranopia (Green-blind)",
  tritanopia: "Tritanopia (Blue-blind)",
  achromatopsia: "Grayscale",
};

const STORAGE_KEY = "colorblind-mode";

export function useColorblindMode() {
  const [mode, setModeState] = useState<ColorblindMode>("normal");

  // Load saved preference on mount
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY) as ColorblindMode | null;
    if (saved && COLORBLIND_LABELS[saved]) {
      setModeState(saved);
    }
  }, []);

  // Apply filter when mode changes
  useEffect(() => {
    // Remove all colorblind classes first
    document.documentElement.classList.remove(
      "colorblind-protanopia",
      "colorblind-deuteranopia",
      "colorblind-tritanopia",
      "colorblind-achromatopsia"
    );

    // Add the appropriate class if not normal
    if (mode !== "normal") {
      document.documentElement.classList.add(`colorblind-${mode}`);
    }
  }, [mode]);

  const setMode = useCallback((newMode: ColorblindMode) => {
    setModeState(newMode);
    localStorage.setItem(STORAGE_KEY, newMode);
  }, []);

  return { mode, setMode };
}
